import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeSVG } from "lit/directives/unsafe-svg.js";

import { BaseElement } from "@/components";
import { decodeBase64Svg, resolveIconSource, resolveImageUrl } from "@/utils";

import type { LogoProps, LogoTheme } from "./logo.types";

@customElement("sb-logo")
export class SbLogo extends BaseElement implements LogoProps {
  @property({ type: String }) id!: string;
  @property({ type: String, attribute: "image-url" }) imageUrl!: string;

  @property({ type: String, reflect: true }) align: string = "center";
  @property({ type: String, reflect: true }) width: string = "";
  @property({ type: String, reflect: true }) height: string = "";

  @state() private theme: LogoTheme = this.getTheme();
  @state() private isSvg = false;

  private observer?: MutationObserver;

  get _containerElement(): HTMLElement | null {
    return this.querySelector(".sb-logo-wrapper");
  }

  connectedCallback() {
    super.connectedCallback();

    this.style.display = "grid";
    this.updateLayoutFromProps();

    this.observer = new MutationObserver(() => {
      this.theme = this.getTheme();
    });

    this.observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
  }

  updated(changed: Map<string, unknown>) {
    super.updated(changed);

    if (changed.has("align") || changed.has("width") || changed.has("height")) {
      this.updateLayoutFromProps();
    }
  }

  private updateLayoutFromProps() {
    const el = this._containerElement as HTMLElement | null;
    if (!el) return;

    if (this.align) {
      el.style.justifySelf = this.align;
    }
    if (this.width) {
      el.style.width = this.width;
    }
    if (this.height) {
      el.style.height = this.height;
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    super.disconnectedCallback();
  }

  private getTheme(): "light" | "dark" {
    return (
      (document.documentElement.getAttribute("data-theme") as
        | "light"
        | "dark") ?? "light"
    );
  }

  protected createRenderRoot() {
    return this;
  }

  render() {
    const resolvedImage = resolveImageUrl(this.imageUrl, this.theme);
    const decodedSvg = resolvedImage ? decodeBase64Svg(resolvedImage) : null;
    this.isSvg = !!decodedSvg;

    return html`
      <div class="sb-logo-wrapper">
        ${decodedSvg
          ? unsafeSVG(decodedSvg)
          : html`
              <img
                part=${this.id ? this.id : "logo"}
                src=${resolveIconSource(resolvedImage)}
                alt="Logo"
              />
            `}
      </div>
    `;
  }
}
